import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';
import { analyticsAPI } from '../lib/api';
import {
  BarChart3,
  Users,
  TrendingUp,
  Award,
  BookOpen,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';

export const ManagerDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await analyticsAPI.getManager();
        setAnalytics(response.data);
      } catch (error) {
        console.error('Failed to fetch manager analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="manager-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <BarChart3 className="w-8 h-8 text-primary" />
            {t('manager.teamAnalytics', 'Team Analytics')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('manager.subtitle', "Monitor your team's learning progress and performance")}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4" data-testid="stat-team-size">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-blue-500/10">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('manager.teamSize', 'Team Size')}</p>
                <p className="text-2xl font-bold">{analytics?.team_size || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4" data-testid="stat-enrollments">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-purple-500/10">
                <BookOpen className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('manager.enrollments', 'Enrollments')}</p>
                <p className="text-2xl font-bold">{analytics?.total_enrollments || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4" data-testid="stat-completed">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-green-500/10">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('courses.completed', 'Completed')}</p>
                <p className="text-2xl font-bold text-green-500">{analytics?.completed_courses || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4" data-testid="stat-completion-rate">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-amber-500/10">
                <TrendingUp className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('manager.avgCompletion', 'Avg. Completion')}</p>
                <p className="text-2xl font-bold">{analytics?.average_completion_rate || 0}%</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performers */}
          <Card data-testid="top-performers">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                {t('manager.topPerformers', 'Top Performers')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.top_performers?.length > 0 ? (
                <div className="space-y-4">
                  {analytics.top_performers.map((member, index) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-4 p-3 rounded-xl bg-muted/50"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        index === 0 ? 'gold-gradient text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-amber-700 text-white' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {index + 1}
                      </div>
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={member.avatar} />
                        <AvatarFallback>{member.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{member.name}</p>
                        <p className="text-sm text-muted-foreground capitalize">
                          {member.role?.replace('_', ' ')}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        <span className="font-bold">{member.points?.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Award className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No team members yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Team Members */}
          <Card data-testid="team-members">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Team Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.team_members?.length > 0 ? (
                <div className="space-y-4">
                  {analytics.team_members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-4 p-3 rounded-xl hover:bg-muted/50 transition-colors"
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={member.avatar} />
                        <AvatarFallback>{member.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{member.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {member.email}
                        </p>
                      </div>
                      <Badge variant="outline" className="capitalize">
                        {member.role?.replace('_', ' ')}
                      </Badge>
                      <div className="flex items-center gap-1 text-sm">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        <span>{member.points}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No team members assigned</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ManagerDashboard;
